import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowRight, CircleNotch, ChartLineUp, MapPin, Sparkle, ArrowUpRight,
  Rocket, TrendUp, Bank, Globe, Handshake, CheckCircle, Spinner, FolderOpen, Plus,
  Scales, Pulse,
} from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import UsageWidget from "@/components/UsageWidget";

const ICON_MAP = {
  rocket: Rocket, trending: TrendUp, building: Bank,
  "chart-up": ChartLineUp, globe: Globe, handshake: Handshake,
};
const ACCENT_MAP = {
  teal: "bg-teal-soft text-teal",
  gold: "bg-gold-soft text-gold",
  navy: "bg-secondary text-primary",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [activeWorkflows, setActiveWorkflows] = useState([]);
  const [fhList, setFhList] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, c, wfs, fh, ir, sub] = await Promise.all([
          api.get("/dashboard"),
          api.get("/workflows/catalog"),
          api.get("/workflows"),
          api.get("/financial-health").catch(() => ({ data: [] })),
          api.get("/investment-readiness/latest").catch(() => ({ data: null })),
          api.get("/subscription/status").catch(() => ({ data: null })),
        ]);
        setData(d.data);
        setCatalog(c.data.workflows);
        setActiveWorkflows(wfs.data);
        setFhList(Array.isArray(fh.data) ? fh.data : []);
        setReadiness(ir.data);
        setSubscription(sub.data);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="p-8 lg:p-12 text-overline text-muted-foreground" data-testid="dashboard-loading">Loading…</div>;
  if (!data) return null;

  const inProgress = activeWorkflows.filter((w) => w.status === "running" || w.status === "pending");
  const recentCompleted = activeWorkflows.filter((w) => w.status === "completed").slice(0, 4);
  const fhReady = fhList.filter((f) => f.status === "ready").length;
  const readinessScore = readiness?.score;

  // Build AI recommendations pool: latest FH recs + latest IR actions
  const aiRecs = [];
  const latestFhReady = fhList.find((f) => f.status === "ready" && f.analysis?.priority_recommendations?.length);
  if (latestFhReady) {
    for (const r of latestFhReady.analysis.priority_recommendations.slice(0, 2)) {
      aiRecs.push({ source: "Financial Health", text: r, link: `/financial-health/${latestFhReady.id}` });
    }
  }
  if (readiness?.improvement_actions?.length) {
    for (const r of readiness.improvement_actions.slice(0, 2)) {
      aiRecs.push({ source: "Readiness", text: r, link: "/investment-readiness" });
    }
  }

  const kpis = [
    { label: "Financial Health", value: fhReady, suffix: fhReady === 1 ? " analysis" : " analyses", icon: Scales, color: "text-teal", bg: "bg-teal-soft", link: "/financial-health" },
    { label: "Investment Readiness", value: readinessScore ?? "—", suffix: readinessScore != null ? "/100" : "", icon: TrendUp, color: "text-gold", bg: "bg-gold-soft", link: "/investment-readiness" },
    { label: "Active Projects", value: inProgress.length, icon: Spinner, color: "text-blue", bg: "bg-blue-soft", link: "/workflows" },
    { label: "AI Recommendations", value: aiRecs.length, icon: Sparkle, color: "text-primary", bg: "bg-secondary", link: aiRecs[0]?.link || "/financial-health" },
    { label: "Markets Covered", value: data.countries_analyzed, icon: MapPin, color: "text-teal", bg: "bg-teal-soft", link: "/radar" },
  ];

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="dashboard-page">
      <PageHeader
        eyebrow="Operating system"
        title="Welcome back"
        subtitle="Continue your projects and investment workflows."
        action={
          <Link to="/workspaces" className="hidden sm:inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors card-elev" data-testid="dashboard-open-workspaces">
            <FolderOpen weight="bold" size={14} /> Open workspaces
          </Link>
        }
      />

      {/* KPIs — 5 cards (v1.1) */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        {kpis.map((k) => (
          <Link key={k.label} to={k.link} className="bg-card border border-border rounded-xl p-4 sm:p-6 card-elev hover:border-primary/40 transition-colors group" data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-start justify-between">
              <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{k.label}</div>
              <div className={`h-7 w-7 sm:h-8 sm:w-8 ${k.bg} ${k.color} rounded-lg grid place-items-center`}>
                <k.icon size={14} weight="bold" />
              </div>
            </div>
            <div className="data-large text-3xl sm:text-4xl mt-3">{k.value}<span className="text-sm text-muted-foreground font-normal">{k.suffix || ""}</span></div>
            <div className="mt-1 text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">Open →</div>
          </Link>
        ))}
      </div>

      {/* Subscription tile (V2) */}
      {subscription && (
        <div className="mt-6 bg-card border border-border rounded-2xl p-5 sm:p-6 card-elev" data-testid="dashboard-subscription">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-11 w-11 rounded-lg bg-teal-soft text-teal grid place-items-center">
                <Sparkle size={20} weight="duotone" />
              </div>
              <div>
                <div className="text-overline text-muted-foreground">Current plan</div>
                <div className="mt-0.5 flex items-baseline gap-2 flex-wrap">
                  <span className="font-display text-xl tracking-tight">Orixa {subscription.plan_label || subscription.plan_key}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${
                    subscription.status === "active" ? "bg-teal-soft text-teal" :
                    subscription.status === "past_due" ? "bg-amber-100 text-amber-700" :
                    "bg-secondary text-muted-foreground"
                  }`}>{subscription.status}</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {subscription.next_payment_date
                    ? `Renews ${new Date(subscription.next_payment_date).toLocaleDateString()}`
                    : subscription.period_resets_at
                      ? `Quota resets ${new Date(subscription.period_resets_at).toLocaleDateString()}`
                      : null}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Link to="/billing" className="flex-1 sm:flex-none text-center border border-border rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-secondary" data-testid="dashboard-billing-link">
                Manage plan
              </Link>
              {subscription.plan_key === "explorer" || subscription.plan_key === "discovery" || subscription.plan_key === "starter" ? (
                <Link to="/pricing" className="flex-1 sm:flex-none text-center bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 card-elev" data-testid="dashboard-upgrade">
                  {subscription.plan_key === "starter" ? "Upgrade to Professional" : "Upgrade"}
                </Link>
              ) : subscription.plan_key === "enterprise" ? (
                <a href="https://cal.com/romuald-ouattara-wttutm/call-orixa-invest" target="_blank" rel="noopener noreferrer" className="flex-1 sm:flex-none text-center border border-border rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-secondary" data-testid="dashboard-sales">
                  Book a product demo
                </a>
              ) : null}
            </div>
          </div>
          {/* Quota usage row */}
          <div className="mt-4 pt-4 border-t border-border">
            <UsageWidget status={subscription} variant="compact" />
          </div>
        </div>
      )}

      {/* AI Recommendations row */}
      {aiRecs.length > 0 && (
        <div className="mt-8 bg-card border border-border rounded-2xl p-5 sm:p-6" data-testid="dashboard-ai-recs">
          <div className="inline-flex items-center gap-2 text-overline text-gold mb-3">
            <Sparkle size={12} weight="fill" /> AI Recommendations
          </div>
          <ul className="space-y-2.5">
            {aiRecs.slice(0, 4).map((r, i) => (
              <li key={`aic-${i}`} className="flex items-start gap-3">
                <div className="text-[9px] font-bold uppercase tracking-wider bg-secondary rounded px-2 py-0.5 shrink-0 mt-0.5 text-muted-foreground">{r.source}</div>
                <Link to={r.link} className="text-sm leading-relaxed hover:text-primary flex-1">{r.text}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}


      {/* My Objectives */}
      <div className="mt-12">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="text-overline text-primary mb-2">My objectives</div>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight">Choose what to execute next</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border" data-testid="dashboard-objectives-grid">
          {catalog.map((c) => {
            const Icon = ICON_MAP[c.icon] || Sparkle;
            return (
              <Link
                key={c.id}
                to={`/workflows/${c.id}`}
                className="bg-background hover:bg-card transition-colors p-5 flex items-start gap-3"
                data-testid={`dashboard-objective-${c.id}`}
              >
                <div className={`h-10 w-10 rounded-md grid place-items-center flex-shrink-0 ${ACCENT_MAP[c.accent] || ACCENT_MAP.navy}`}>
                  <Icon size={18} weight="duotone" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm leading-tight">{c.label}</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-snug">{c.tagline}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Active workflows */}
      {activeWorkflows.length > 0 && (
        <div className="mt-12">
          <div className="flex items-end justify-between mb-5">
            <div>
              <div className="text-overline text-primary mb-2">In progress</div>
              <h2 className="font-display text-2xl sm:text-3xl tracking-tight">Active workflows</h2>
            </div>
            <Link to="/workspaces" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 font-medium">View all <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="active-workflows-list">
            {activeWorkflows.slice(0, 4).map((w) => {
              const Icon = ICON_MAP[w.icon] || Sparkle;
              const completed = (w.steps || []).filter((s) => s.status === "completed").length;
              const total = (w.steps || []).length;
              return (
                <Link key={w.id} to={`/workflows/run/${w.id}`}
                  className="bg-card border border-border rounded-xl p-5 hover:card-elev transition-shadow flex items-start gap-3"
                  data-testid={`active-workflow-${w.id}`}
                >
                  <div className={`h-10 w-10 rounded-lg grid place-items-center flex-shrink-0 ${ACCENT_MAP[w.accent] || ACCENT_MAP.navy}`}>
                    <Icon size={20} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold text-sm truncate">{w.label}</div>
                      {w.status === "completed" && <CheckCircle size={16} weight="fill" className="text-teal flex-shrink-0" />}
                      {w.status === "running" && <Spinner size={14} className="animate-spin text-primary flex-shrink-0" />}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {completed}/{total} · {w.progress}%
                    </div>
                    <div className="h-1 bg-secondary rounded-full overflow-hidden mt-2">
                      <div className={`h-full ${w.status === "failed" ? "bg-rose-500" : "bg-primary"}`}
                        style={{ width: `${w.progress}%` }} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Dossiers pipeline (legacy assessments) */}
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
        <div className="lg:col-span-2">
          <div className="flex items-end justify-between mb-4">
            <h2 className="font-display text-xl sm:text-2xl tracking-tight">Recent dossiers</h2>
            <Link to="/assessments" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 font-medium" data-testid="dashboard-view-all">View all <ArrowRight size={14} /></Link>
          </div>
          <div className="hidden sm:block bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-secondary">
                <tr>
                  <th className="text-left p-4 text-overline text-muted-foreground">Title</th>
                  <th className="text-left p-4 text-overline text-muted-foreground">Countries</th>
                  <th className="text-left p-4 text-overline text-muted-foreground">Sector</th>
                  <th className="text-left p-4 text-overline text-muted-foreground">Status</th>
                  <th className="text-right p-4 text-overline text-muted-foreground">Score</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_assessments.length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-sm text-muted-foreground">No dossiers yet. Pick an objective above to begin.</td></tr>
                )}
                {data.recent_assessments.map((a) => (
                  <tr key={a.id} className="border-t border-border hover:bg-secondary/50 transition-colors">
                    <td className="p-4"><Link to={`/assessments/${a.id}`} className="font-medium hover:text-primary">{a.title}</Link></td>
                    <td className="p-4 text-sm text-muted-foreground">{a.countries.slice(0, 2).join(", ")}{a.countries.length > 2 ? ` +${a.countries.length - 2}` : ""}</td>
                    <td className="p-4 text-sm text-muted-foreground">{a.sector}</td>
                    <td className="p-4"><StatusBadge status={a.status} /></td>
                    <td className="p-4 text-right data-large text-lg text-primary">{a.scores?.opportunity_score || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="space-y-3 sm:hidden">
            {data.recent_assessments.length === 0 && (
              <div className="bg-card border border-border rounded-xl p-8 text-center text-sm text-muted-foreground">No dossiers yet.</div>
            )}
            {data.recent_assessments.map((a) => (
              <Link key={a.id} to={`/assessments/${a.id}`} className="block bg-card border border-border rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{a.title}</div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">{a.countries.slice(0, 2).join(", ")} · {a.sector}</div>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div className="text-xs text-muted-foreground font-mono">{a.currency || "€"}{a.budget_eur.toLocaleString()}</div>
                  <div className="data-large text-xl text-primary">{a.scores?.opportunity_score || "—"}<span className="text-xs text-muted-foreground font-normal">/100</span></div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-display text-xl sm:text-2xl tracking-tight mb-4">Sector exposure</h2>
          <div className="bg-card border border-border rounded-xl p-5 sm:p-6">
            {data.sectors.length === 0 && <div className="text-sm text-muted-foreground">No data yet.</div>}
            {data.sectors.slice(0, 8).map((s, i) => {
              const max = Math.max(...data.sectors.map((x) => x.count));
              const pct = (s.count / max) * 100;
              return (
                <div key={s.name} className="mb-4 last:mb-0" data-testid={`sector-row-${i}`}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="truncate pr-2">{s.name}</span>
                    <span className="text-muted-foreground font-mono text-xs">{s.count}</span>
                  </div>
                  <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <a href="https://cal.com/romuald-ouattara-wttutm/call-orixa-invest" target="_blank" rel="noopener noreferrer" className="mt-4 block bg-primary text-primary-foreground rounded-xl p-5 hover:opacity-90 transition card-elev" data-testid="dashboard-book-cta">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 mb-1">Product demo</div>
            <div className="font-display text-lg leading-tight tracking-tight">See Orixa Invest in action</div>
            <div className="text-xs mt-1.5 opacity-80">A 30-minute walkthrough of the platform.</div>
            <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold">Book a product demo <ArrowUpRight size={14} weight="bold" /></div>
          </a>
        </div>
      </div>
    </div>
  );
}
